import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Flex,
  Portal,
  Text,
} from "@chakra-ui/react";
import { useVersionInfoRetrieveSuspense } from "gen/endpoints/version/version";
import React, { useEffect, useMemo, useRef } from "react";
import { boxShadowStyle } from "shared/constants/style";
import { InfoCircleIcon } from "shared/icons/InfoCircleIcon";

export const AppUpdatePrompt: React.FC = () => {
  // kill the existing service worker
  // selfDestroying true should do this, but adding for good measure
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      void navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((reg) => reg.unregister());
      });
    }
  }, []);

  const { data: versionInfo } = useVersionInfoRetrieveSuspense({
    query: {
      refetchInterval: 1000 * 30,
    },
  });

  const appVersionOnLoad = useRef<string | null>(versionInfo.git_sha);

  const needRefresh = useMemo(
    () => appVersionOnLoad.current !== versionInfo.git_sha,
    [appVersionOnLoad, versionInfo.git_sha],
  );

  return needRefresh ? (
    <Portal>
      <Flex
        position="fixed"
        top="24px"
        left="24px"
        right="24px"
        alignItems="center"
        justifyContent="center"
        filter="brightness(1)"
        _hover={{
          filter: "brightness(0.95)", // Darkens on hover
        }}
      >
        <Alert
          border="none"
          status="warning"
          data-ls-enabled={true}
          data-public={true}
          onClick={() => window.location.reload()}
          py="28px"
          boxShadow={boxShadowStyle}
        >
          <AlertIcon>
            <InfoCircleIcon />
          </AlertIcon>
          <Flex justifyContent="space-between" alignItems="center" w="100%">
            <Box mx="16px" display="flex" flexDirection="column" gap="8px">
              <AlertTitle flexGrow="0">
                <Text textStyle="h4" color="warning.900">
                  New Version of Maybern Available
                </Text>
              </AlertTitle>
              <AlertDescription>
                <Text textStyle="bodyBold" color="warning.900">
                  If you’re in the middle of a workflow submit current page
                  before updating. Some features may not work as expected until
                  app is updated
                </Text>
              </AlertDescription>
            </Box>
            <Button variant="text" color="warning.900">
              <Text textStyle="bodyBold">Update Here</Text>
            </Button>
          </Flex>
        </Alert>
      </Flex>
    </Portal>
  ) : null;
};
